import React, { useState, useEffect, useRef } from 'react';
import useCollapseDrawer from '../../../hooks/useCollapseDrawer';
import admin_api from '../../../api/admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  IconButton,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Chip,
  Alert,
  Snackbar,
  Card,
  CardContent,
  StepConnector,
  Tooltip,
  InputAdornment,
  Skeleton,
  Fade,
  Slide
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ArrowBack,
  ArrowForward,
  CheckCircle,
  Close,
  AddPhotoAlternate,
  SportsTennis,
  SportsBasketball,
  SportsSoccer,
  SportsCricket,
  MonetizationOn,
  AccessTime,
  LocationOn,
  Description,
  Check,
  Info,
  Visibility,
  VisibilityOff,
  Search,
  FilterList,
  Refresh
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import SecureLS from 'secure-ls';
import { getImageUrl, processCourtImages } from '../../../utils/imageUtils';

// Styled components with dark theme
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
  background: '#1a2235',
  color: '#ffffff',
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    backgroundColor: '#1a2235',
    color: '#ffffff',
    borderRadius: theme.shape.borderRadius,
    minWidth: '80vw',
    maxWidth: '90vw',
  },
}));

const StyledStepConnector = styled(StepConnector)(({ theme }) => ({
  '& .MuiStepConnector-line': {
    borderColor: '#06b6d4',
    borderTopWidth: 3,
  },
}));

const StyledStepLabel = styled(StepLabel)(({ theme }) => ({
  '& .MuiStepLabel-label': {
    marginTop: theme.spacing(1),
    fontWeight: 500,
    color: '#e2e8f0',
  },
  '& .MuiStepLabel-label.Mui-active': {
    color: '#06b6d4',
    fontWeight: 700,
  },
  '& .MuiStepLabel-label.Mui-completed': {
    color: '#06b6d4',
    fontWeight: 700,
  },
}));

const StyledFormSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: '#1e293b',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.15)',
  color: '#ffffff',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: '#2d3748',
    },
    '&:hover fieldset': {
      borderColor: '#4a5568',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#06b6d4',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#a0aec0',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#06b6d4',
  },
  '& .MuiOutlinedInput-input': {
    color: '#e2e8f0',
  },
  '& .MuiInputAdornment-root .MuiSvgIcon-root': {
    color: '#06b6d4',
  },
  '& .MuiFormHelperText-root': {
    color: '#a0aec0',
  },
  '& .MuiFormHelperText-root.Mui-error': {
    color: '#f56565',
  },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiInputLabel-root': {
    color: '#a0aec0',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#06b6d4',
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: '#2d3748',
    },
    '&:hover fieldset': {
      borderColor: '#4a5568',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#06b6d4',
    },
  },
  '& .MuiSelect-select': {
    color: '#e2e8f0',
  },
  '& .MuiSvgIcon-root': {
    color: '#a0aec0',
  },
}));

const ImagePreviewContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  marginTop: theme.spacing(2),
}));

const ImagePreview = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: 120,
  height: 120,
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  border: '1px solid #2d3748',
}));

export default function ManageCourts() {
  const [courts, setCourts] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedCourt, setSelectedCourt] = useState(null);
  const [confirmationInput, setConfirmationInput] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');

  // Multi-step form states
  const [activeStep, setActiveStep] = useState(0);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [previewImages, setPreviewImages] = useState([]);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    courtName: '',
    location: '',
    courtType: '',
    description: '',
    facilities: [],
    rate: '',
    openTime: '08:00',
    closeTime: '22:00',
    availableDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
    maintenanceDay: '',
    images: []
  });

  const { isCollapse } = useCollapseDrawer();
  const DRAWER_WIDTH = 280;
  const COLLAPSE_WIDTH = 102;

  // Steps definition
  const steps = [
    {
      label: 'Basic Info',
      icon: <LocationOn color="primary" />,
      description: 'Name and location'
    },
    {
      label: 'Details',
      icon: <SportsTennis color="primary" />,
      description: 'Type and facilities'
    },
    {
      label: 'Pricing',
      icon: <MonetizationOn color="primary" />,
      description: 'Rates and hours'
    },
    {
      label: 'Images',
      icon: <AddPhotoAlternate color="primary" />,
      description: 'Upload photos'
    }
  ];

  const daysOfWeek = [
    'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
  ];

  const courtTypes = [
    { value: 'tennis', label: 'Tennis', icon: <SportsTennis /> },
    { value: 'basketball', label: 'Basketball', icon: <SportsBasketball /> },
    { value: 'volleyball', label: 'Volleyball', icon: <SportsSoccer /> },
    { value: 'badminton', label: 'Badminton', icon: <SportsCricket /> },
  ];

  // Filter courts based on search and type
  const filteredCourts = Array.isArray(courts) ? 
    courts.filter(court => {
      const matchesSearch = !searchQuery || 
        court.courtName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        court.location?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesType = filterType === 'all' || court.courtType === filterType;
      
      return matchesSearch && matchesType;
    }) : [];

  const displayedCourts = filteredCourts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const resetForm = () => {
    setFormData({
      courtName: '',
      location: '',
      courtType: '',
      description: '',
      facilities: [],
      rate: '',
      openTime: '08:00',
      closeTime: '22:00',
      availableDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
      maintenanceDay: '',
      images: []
    });
    setPreviewImages([]);
    setActiveStep(0);
    setValidationErrors({});
    setFormError('');
  };

  const fetchCourts = async () => {
    try {
      setLoading(true);
      
      const response = await admin_api.get_admin_courts();
      
      if (response && response.data) {
        let courtsData;
        
        if (Array.isArray(response.data)) {
          courtsData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          courtsData = response.data.data;
        } else {
          console.warn('Unexpected API response format:', response.data);
          courtsData = [];
        }
        
        const formattedCourts = courtsData.map(court => ({
          _id: court.id || court._id,
          id: court.id || court._id,
          courtId: court.court_id || court.courtId,
          courtName: court.court_name || court.courtName,
          location: court.location,
          description: court.description,
          rate: court.hourly_rate || court.rate || 0,
          // Fix: Extract courtType from features object
          courtType: court.features?.courtType || court.court_type || court.courtType || 'tennis',
          // Fix: Extract availableDays from features object
          availableDays: court.features?.availableDays || court.available_days || court.availableDays || [],
          facilities: court.amenities || court.facilities || [], // Also map amenities to facilities
          openTime: court.opening_time || court.open_time || court.openTime || '08:00',
          closeTime: court.closing_time || court.close_time || court.closeTime || '22:00',
          maintenanceDay: court.maintenance_day || court.maintenanceDay || '',
          images: court.images || court.image_urls || [],
          status: court.status || 'pending',
          created_by: court.created_by || court.createdBy
        }));
        
        setCourts(formattedCourts);
      } else {
        setCourts([]);
      }
      setError(null);
    } catch (error) {
      console.error('Error fetching courts:', error);
      setError('Failed to fetch courts. Please try again later.');
      setCourts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (court = null) => {
    setSelectedCourt(court);
    
    if (court) {
      setFormData({
        courtName: court.courtName || '',
        location: court.location || '',
        courtType: court.courtType || '',
        description: court.description || '',
        facilities: court.facilities || [],
        rate: court.rate || '',
        openTime: court.openTime || '08:00',
        closeTime: court.closeTime || '22:00',
        availableDays: court.availableDays || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
        maintenanceDay: court.maintenanceDay || '',
        images: court.images || []
      });
      setPreviewImages(court.images || []);
    } else {
      resetForm();
    }
    
    setOpenDialog(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'rate') {
      const numericValue = value === '' ? '' : parseFloat(value);
      setFormData(prev => ({
        ...prev,
        [name]: numericValue || value
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFacilityAdd = (facility) => {
    if (facility && !formData.facilities.includes(facility)) {
      setFormData(prev => ({
        ...prev,
        facilities: [...prev.facilities, facility]
      }));
    }
  };

  const handleFacilityDelete = (facility) => {
    setFormData(prev => ({
      ...prev,
      facilities: prev.facilities.filter(f => f !== facility)
    }));
  };

  const handleDayToggle = (day) => {
    const updatedDays = formData.availableDays.includes(day)
      ? formData.availableDays.filter(d => d !== day)
      : [...formData.availableDays, day];
    
    setFormData(prev => ({
      ...prev,
      availableDays: updatedDays
    }));
  };

  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 800;
          const MAX_HEIGHT = 600;
          let width = img.width;
          let height = img.height;
          
          if (width > height) {
            if (width > MAX_WIDTH) {
              height = height * (MAX_WIDTH / width);
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width = width * (MAX_HEIGHT / height);
              height = MAX_HEIGHT;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
          resolve(dataUrl);
        };
        
        img.onerror = () => reject(new Error('Failed to load image'));
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
    });
  };

  const handleImageUpload = async (e) => {
    try {
      setFormLoading(true);
      
      const files = Array.from(e.target.files);
      if (files.length === 0) return;
      
      const MAX_FILES = 5;
      const filesToProcess = files.slice(0, MAX_FILES);
      
      for (const file of filesToProcess) {
        try {
          const compressedImage = await compressImage(file);
          
          setPreviewImages(prev => [...prev, compressedImage]);
          setFormData(prev => ({
            ...prev,
            images: [...prev.images, compressedImage]
          }));
        } catch (err) {
          console.error('Error processing image:', err);
        }
      }
    } catch (error) {
      console.error('Image upload error:', error);
    } finally {
      setFormLoading(false);
    }
  };

  const handleRemoveImage = (index) => {
    setPreviewImages(prev => prev.filter((_, i) => i !== index));
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const validateStep = (step) => {
    const errors = {};
    
    switch (step) {
      case 0:
        if (!formData.courtName.trim()) errors.courtName = 'Court name is required';
        if (!formData.location.trim()) errors.location = 'Location is required';
        break;
      case 1:
        if (!formData.courtType) errors.courtType = 'Court type is required';
        if (!formData.description.trim()) errors.description = 'Description is required';
        break;
      case 2:
        if (!formData.rate) errors.rate = 'Price is required';
        else if (isNaN(parseFloat(formData.rate))) errors.rate = 'Price must be a number';
        if (!formData.openTime) errors.openTime = 'Opening time is required';
        if (!formData.closeTime) errors.closeTime = 'Closing time is required';
        if (formData.availableDays.length === 0) errors.availableDays = 'At least one available day is required';
        break;
      case 3:
        if (formData.images.length === 0 && !selectedCourt) errors.images = 'At least one image is required';
        break;
      default:
        break;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleFormSubmit = async () => {
    try {
      setFormLoading(true);
      setFormError('');
      
      if (isNaN(parseFloat(formData.rate))) {
        setFormError('Rate must be a valid number');
        setFormLoading(false);
        return;
      }
      
      const apiData = {
        courtName: formData.courtName,
        location: formData.location,
        description: formData.description,
        rate: parseFloat(formData.rate),
        openTime: formData.openTime,
        closeTime: formData.closeTime,
        courtType: formData.courtType,
        facilities: formData.facilities,
        availableDays: formData.availableDays,
        maintenanceDay: formData.maintenanceDay,
        images: formData.images
      };
      
      if (selectedCourt) {
        await admin_api.update_court(selectedCourt._id || selectedCourt.id, apiData);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        await admin_api.create_court(apiData);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      }
      
      await fetchCourts();
      setOpenDialog(false);
      resetForm();
    } catch (error) {
      console.error('Error saving court:', error);
      setFormError(error.message || 'Failed to save court. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteClick = (court) => {
    setSelectedCourt(court);
    setConfirmationInput('');
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (confirmationInput !== selectedCourt.courtName) {
      return;
    }

    try {
      await admin_api.delete_court(selectedCourt._id || selectedCourt.id);
      await fetchCourts();
      setOpenDeleteDialog(false);
      setConfirmationInput('');
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error deleting court:', error);
      setError('Error deleting court');
    }
  };

  useEffect(() => {
    fetchCourts();
  }, []);

  const containerStyle = {
    padding: '20px',
    maxWidth: '100%',
    marginTop: '80px',
    marginLeft: isCollapse ? `${COLLAPSE_WIDTH + 24}px` : `${DRAWER_WIDTH + 24}px`,
    marginRight: '24px',
    transition: 'all 0.3s ease',
    backgroundColor: '#12192c',
    minHeight: 'calc(100vh - 100px)',
  };

  if (window.innerWidth <= 1024) {
    containerStyle.marginLeft = '24px';
  }

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <StyledFormSection>
            <Typography variant="h6" gutterBottom sx={{ color: '#06b6d4', fontWeight: 'bold' }}>
              Basic Court Information
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: '#a0aec0' }}>
              Enter the basic details about your court.
            </Typography>
            
            <StyledTextField
              fullWidth
              margin="normal"
              label="Court Name"
              name="courtName"
              value={formData.courtName}
              onChange={handleInputChange}
              error={!!validationErrors.courtName}
              helperText={validationErrors.courtName || "Enter a clear, descriptive name"}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SportsTennis sx={{ color: '#06b6d4' }} />
                  </InputAdornment>
                ),
              }}
            />
            
            <StyledTextField
              fullWidth
              margin="normal"
              label="Location"
              name="location"
              value={formData.location}
              onChange={handleInputChange}
              error={!!validationErrors.location}
              helperText={validationErrors.location || "Provide the full address"}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOn sx={{ color: '#06b6d4' }} />
                  </InputAdornment>
                ),
              }}
            />
          </StyledFormSection>
        );

      case 1:
        return (
          <StyledFormSection>
            <Typography variant="h6" gutterBottom sx={{ color: '#06b6d4', fontWeight: 'bold' }}>
              Court Details
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: '#a0aec0' }}>
              Specify court type and available facilities.
            </Typography>
            
            <StyledFormControl fullWidth error={!!validationErrors.courtType} sx={{ mb: 3 }}>
              <InputLabel>Court Type</InputLabel>
              <Select
                name="courtType"
                value={formData.courtType}
                onChange={handleInputChange}
              >
                {courtTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {type.icon}
                      <Box sx={{ ml: 1 }}>{type.label}</Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {validationErrors.courtType && (
                <FormHelperText error>{validationErrors.courtType}</FormHelperText>
              )}
            </StyledFormControl>
            
            <StyledTextField
              fullWidth
              multiline
              rows={4}
              margin="normal"
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              error={!!validationErrors.description}
              helperText={validationErrors.description || "Describe your court details"}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                    <Description sx={{ color: '#06b6d4' }} />
                  </InputAdornment>
                ),
              }}
            />
            
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, color: '#e2e8f0' }}>
              Facilities
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {formData.facilities.map((facility) => (
                <Chip
                  key={facility}
                  label={facility}
                  onDelete={() => handleFacilityDelete(facility)}
                  sx={{ 
                    backgroundColor: 'rgba(6, 182, 212, 0.1)',
                    color: '#06b6d4', 
                    borderColor: '#06b6d4',
                  }}
                />
              ))}
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <StyledTextField
                label="Add Facility"
                placeholder="e.g., Changing rooms"
                size="small"
                id="new-facility"
                sx={{ flex: 1 }}
              />
              <Button 
                variant="contained" 
                onClick={() => {
                  const input = document.getElementById('new-facility');
                  handleFacilityAdd(input.value);
                  input.value = '';
                }}
                sx={{ backgroundColor: '#06b6d4', '&:hover': { backgroundColor: '#0891b2' } }}
              >
                Add
              </Button>
            </Box>
          </StyledFormSection>
        );

      case 2:
        return (
          <StyledFormSection>
            <Typography variant="h6" gutterBottom sx={{ color: '#06b6d4', fontWeight: 'bold' }}>
              Pricing & Availability
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: '#a0aec0' }}>
              Set operating hours and pricing.
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <StyledTextField
                  fullWidth
                  type="number"
                  label="Price per Hour ($)"
                  name="rate"
                  value={formData.rate}
                  onChange={handleInputChange}
                  error={!!validationErrors.rate}
                  helperText={validationErrors.rate}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MonetizationOn sx={{ color: '#06b6d4' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <StyledFormControl fullWidth>
                  <InputLabel>Maintenance Day</InputLabel>
                  <Select
                    name="maintenanceDay"
                    value={formData.maintenanceDay}
                    onChange={handleInputChange}
                  >
                    <MenuItem value="">None</MenuItem>
                    {daysOfWeek.map((day) => (
                      <MenuItem key={day.toLowerCase()} value={day.toLowerCase()}>
                        {day}
                      </MenuItem>
                    ))}
                  </Select>
                </StyledFormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <StyledTextField
                  fullWidth
                  type="time"
                  label="Opening Time"
                  name="openTime"
                  value={formData.openTime}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  error={!!validationErrors.openTime}
                  helperText={validationErrors.openTime}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccessTime sx={{ color: '#06b6d4' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <StyledTextField
                  fullWidth
                  type="time"
                  label="Closing Time"
                  name="closeTime"
                  value={formData.closeTime}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  error={!!validationErrors.closeTime}
                  helperText={validationErrors.closeTime}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccessTime sx={{ color: '#06b6d4' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
            
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, color: '#e2e8f0' }}>
              Available Days
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {daysOfWeek.map((day) => {
                const dayLower = day.toLowerCase();
                const isSelected = formData.availableDays.includes(dayLower);
                const isMaintenanceDay = formData.maintenanceDay === dayLower;
                
                return (
                  <Chip
                    key={day}
                    label={day}
                    onClick={() => !isMaintenanceDay && handleDayToggle(dayLower)}
                    color={isSelected ? "primary" : "default"}
                    variant={isSelected ? "filled" : "outlined"}
                    disabled={isMaintenanceDay}
                    sx={{ 
                      opacity: isMaintenanceDay ? 0.5 : 1,
                      cursor: isMaintenanceDay ? 'not-allowed' : 'pointer',
                      backgroundColor: isSelected ? 'rgba(6, 182, 212, 0.8)' : 'transparent',
                      color: isSelected ? '#ffffff' : '#e2e8f0',
                      borderColor: isSelected ? '#06b6d4' : '#4a5568',
                    }}
                  />
                );
              })}
            </Box>
            
            {validationErrors.availableDays && (
              <Typography variant="caption" color="error">
                {validationErrors.availableDays}
              </Typography>
            )}
          </StyledFormSection>
        );

      case 3:
        return (
          <StyledFormSection>
            <Typography variant="h6" gutterBottom sx={{ color: '#06b6d4', fontWeight: 'bold' }}>
              Court Images
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: '#a0aec0' }}>
              Upload photos of your court (up to 5 images).
            </Typography>
            
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleImageUpload}
              style={{ display: 'none' }}
              ref={fileInputRef}
            />
            
            <Button
              variant="outlined"
              onClick={() => fileInputRef.current?.click()}
              startIcon={<AddPhotoAlternate />}
              disabled={formLoading || previewImages.length >= 5}
              sx={{ 
                mb: 2,
                borderColor: '#06b6d4',
                color: '#06b6d4',
                '&:hover': {
                  borderColor: '#0891b2',
                  backgroundColor: 'rgba(6, 182, 212, 0.1)',
                }
              }}
            >
              {formLoading ? 'Processing...' : 'Upload Images'}
            </Button>
            
            {previewImages.length > 0 && (
              <ImagePreviewContainer>
                {previewImages.map((image, index) => (
                  <ImagePreview key={index}>
                    <img
                      src={image}
                      alt={`Preview ${index + 1}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveImage(index)}
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.9)',
                        },
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </ImagePreview>
                ))}
              </ImagePreviewContainer>
            )}
            
            {validationErrors.images && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                {validationErrors.images}
              </Typography>
            )}
          </StyledFormSection>
        );

      default:
        return 'Unknown step';
    }
  };

  return (
    <div style={containerStyle}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ color: '#ffffff', fontWeight: 'bold' }}>
          Manage Courts
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ 
            backgroundColor: '#06b6d4',
            '&:hover': { backgroundColor: '#0891b2' },
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Add New Court
        </Button>
      </Box>

      {/* Search and Filter Controls */}
      <StyledPaper sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <StyledTextField
              fullWidth
              size="small"
              placeholder="Search courts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: '#06b6d4' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <StyledFormControl fullWidth size="small">
              <InputLabel>Filter by Type</InputLabel>
              <Select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <FilterList sx={{ color: '#06b6d4' }} />
                  </InputAdornment>
                }
              >
                <MenuItem value="all">All Types</MenuItem>
                {courtTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </StyledFormControl>
          </Grid>
          <Grid item xs={12} sm={2} md={2}>
            <Button
              variant="outlined"
              onClick={fetchCourts}
              startIcon={<Refresh />}
              sx={{ 
                borderColor: '#06b6d4',
                color: '#06b6d4',
                '&:hover': {
                  borderColor: '#0891b2',
                  backgroundColor: 'rgba(6, 182, 212, 0.1)',
                }
              }}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </StyledPaper>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress sx={{ color: '#06b6d4' }} />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, backgroundColor: '#1e293b', color: '#ffffff' }}>
          {error}
        </Alert>
      )}

      {/* Courts Table */}
      {!loading && !error && (
        <StyledPaper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: '#06b6d4', fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ color: '#06b6d4', fontWeight: 'bold' }}>Court Name</TableCell>
                  <TableCell sx={{ color: '#06b6d4', fontWeight: 'bold' }}>Location</TableCell>
                  <TableCell sx={{ color: '#06b6d4', fontWeight: 'bold' }}>Type</TableCell>
                  <TableCell sx={{ color: '#06b6d4', fontWeight: 'bold' }}>Rate</TableCell>
                  <TableCell sx={{ color: '#06b6d4', fontWeight: 'bold' }}>Hours</TableCell>
                  <TableCell sx={{ color: '#06b6d4', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedCourts.length > 0 ? (
                  displayedCourts.map((court) => (
                    <TableRow key={court._id || court.id} hover>
                      <TableCell>
                        <Chip
                          size="small"
                          label={court.status}
                          sx={{
                            textTransform: 'capitalize',
                            fontWeight: 600,
                            backgroundColor:
                              court.status === 'approved'
                                ? 'rgba(34, 197, 94, 0.2)'
                                : court.status === 'rejected'
                                ? 'rgba(239, 68, 68, 0.2)'
                                : 'rgba(234, 179, 8, 0.2)',
                            color:
                              court.status === 'approved'
                                ? '#22c55e'
                                : court.status === 'rejected'
                                ? '#ef4444'
                                : '#eab308',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: '#e2e8f0' }}>
                        <Typography variant="body2" fontWeight="medium">
                          {court.courtName}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ color: '#a0aec0' }}>
                        <Typography variant="body2">{court.location}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={court.courtType}
                          sx={{
                            backgroundColor: 'rgba(6, 182, 212, 0.1)',
                            color: '#06b6d4',
                            textTransform: 'capitalize',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: '#e2e8f0' }}>
                        <Typography variant="body2" fontWeight="medium">
                          ${court.rate}/hr
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ color: '#a0aec0' }}>
                        <Typography variant="body2">
                          {court.openTime} - {court.closeTime}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Edit Court">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(court)}
                              sx={{ 
                                color: '#06b6d4',
                                '&:hover': { backgroundColor: 'rgba(6, 182, 212, 0.1)' }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Court">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(court)}
                              sx={{ 
                                color: '#f56565',
                                '&:hover': { backgroundColor: 'rgba(245, 101, 101, 0.1)' }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" sx={{ color: '#a0aec0' }}>
                        {searchQuery || filterType !== 'all' ? 'No courts match your search criteria.' : 'No courts found. Add your first court to get started!'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    colSpan={6}
                    count={filteredCourts.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{ color: '#a0aec0' }}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </StyledPaper>
      )}

      {/* Add/Edit Court Dialog */}
      <StyledDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ borderBottom: '1px solid #2d3748', pb: 2 }}>
          <Typography variant="h5" sx={{ color: '#06b6d4', fontWeight: 'bold' }}>
            {selectedCourt ? 'Edit Court' : 'Add New Court'}
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          <Stepper 
            activeStep={activeStep} 
            connector={<StyledStepConnector />}
            sx={{ mb: 4 }}
          >
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StyledStepLabel>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {step.icon}
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {step.label}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#a0aec0' }}>
                        {step.description}
                      </Typography>
                    </Box>
                  </Box>
                </StyledStepLabel>
              </Step>
            ))}
          </Stepper>

          {formError && (
            <Alert severity="error" sx={{ mb: 3, backgroundColor: '#1e293b', color: '#ffffff' }}>
              {formError}
            </Alert>
          )}

          <Fade in={true} key={activeStep}>
            <Box>{getStepContent(activeStep)}</Box>
          </Fade>
        </DialogContent>

        <DialogActions sx={{ borderTop: '1px solid #2d3748', pt: 2, px: 3, pb: 3 }}>
          <Button
            onClick={() => setOpenDialog(false)}
            sx={{ color: '#a0aec0' }}
          >
            Cancel
          </Button>
          
          {activeStep > 0 && (
            <Button
              onClick={handleBack}
              startIcon={<ArrowBack />}
              sx={{ color: '#06b6d4' }}
            >
              Back
            </Button>
          )}
          
          {activeStep < steps.length - 1 ? (
            <Button
              onClick={handleNext}
              endIcon={<ArrowForward />}
              variant="contained"
              sx={{ 
                backgroundColor: '#06b6d4',
                '&:hover': { backgroundColor: '#0891b2' }
              }}
            >
              Next
            </Button>
          ) : (
            <Button
              onClick={handleFormSubmit}
              disabled={formLoading}
              startIcon={formLoading ? <CircularProgress size={20} /> : <CheckCircle />}
              variant="contained"
              sx={{ 
                backgroundColor: '#06b6d4',
                '&:hover': { backgroundColor: '#0891b2' }
              }}
            >
              {formLoading ? 'Saving...' : (selectedCourt ? 'Update Court' : 'Create Court')}
            </Button>
          )}
        </DialogActions>
      </StyledDialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        PaperProps={{
          sx: { backgroundColor: '#1a2235', color: '#ffffff' }
        }}
      >
        <DialogTitle sx={{ color: '#f56565' }}>
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to delete "{selectedCourt?.courtName}"? This action cannot be undone.
          </Typography>
          <Typography sx={{ mb: 2, fontWeight: 'bold' }}>
            Type the court name to confirm:
          </Typography>
          <StyledTextField
            fullWidth
            value={confirmationInput}
            onChange={(e) => setConfirmationInput(e.target.value)}
            placeholder={selectedCourt?.courtName}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} sx={{ color: '#a0aec0' }}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            disabled={confirmationInput !== selectedCourt?.courtName}
            sx={{ color: '#f56565' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSuccess(false)} 
          severity="success"
          sx={{ backgroundColor: '#1e293b', color: '#ffffff' }}
        >
          Operation completed successfully!
        </Alert>
      </Snackbar>
    </div>
  );
}